import { useFeatureEntitlements } from '@frontegg/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isDebugMode } from '../../debug/debugUser';
import { MaggieFeatureFlags } from '../../util/feature-flags';
import { FROST_FEATURE_KEYS, FrostTarget } from './frosted-gate.constants';

type FeatureEntitlement = Pick<ReturnType<typeof useFeatureEntitlements>, 'isEntitled'>;

/** No session means no plan to read, so a debug user sees every module rather than none. */
const DEBUG_ENTITLEMENT: FeatureEntitlement = { isEntitled: true };

/**
 * Debug mode mounts the app outside `FronteggProvider`, where the entitlements hook finds no store
 * and throws. Picked once at module load because debug mode itself only changes on reload, which
 * keeps the call in `useIsFrosted` unconditional.
 */
const useFeatureEntitlement: (key: string) => FeatureEntitlement = isDebugMode()
  ? () => DEBUG_ENTITLEMENT
  : useFeatureEntitlements;

/**
 * The flag is the rollout switch and the Frontegg feature decides per target. Existing
 * permission and flag gates stay in place until every client carries a plan.
 */
export function isFrosted(flags: Partial<MaggieFeatureFlags>, entitlement: FeatureEntitlement): boolean {
  return Boolean(flags.showFrostedModules) && !entitlement.isEntitled;
}

export function useIsFrosted(target: FrostTarget): boolean {
  const flags = useFlags<MaggieFeatureFlags>();
  const entitlement = useFeatureEntitlement(FROST_FEATURE_KEYS[target]);
  return isFrosted(flags, entitlement);
}
